import { useState, useEffect } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Map, 
  CheckCircle2, 
  Circle, 
  Lock,
  ChevronRight,
  Crown,
  BookOpen,
  Sparkles,
  TrendingUp
} from "lucide-react";

export function LearningPathCard({ 
  studentId,
  grade = 5, 
  subject = "math", 
  planType = "starter", 
  onUpgradeClick 
}) {
  const [loading, setLoading] = useState(true);
  const [learningPath, setLearningPath] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentConcept, setCurrentConcept] = useState(null);

  useEffect(() => {
    fetchLearningPath();
  }, [grade, subject]);

  const fetchLearningPath = async () => {
    try {
      // Fetch learning path for the grade/subject
      const pathRes = await fetch(`${API}/conversation/governed/learning-paths/${grade}/${subject}`);
      const pathData = await pathRes.json();
      
      // Handle both response formats: {success, path} or {status, concepts}
      if (pathData.success || pathData.status === 'ok') {
        const concepts = pathData.path || pathData.concepts || [];
        setLearningPath(concepts);
      }

      // Fetch progress if student ID provided
      if (studentId) {
        const progressRes = await fetch(
          `${API}/conversation/governed/learning-paths/progress/${studentId}/${grade}/${subject}`,
          { credentials: "include" }
        );
        const progressData = await progressRes.json();
        
        if ((progressData.success || progressData.status === 'ok') && progressData.progress) {
          setProgress(progressData.progress);
          setCurrentConcept(progressData.progress.current_concept);
        }
      }
    } catch (error) {
      console.error("Failed to fetch learning path:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determine visibility based on plan
  const getViewLevel = () => {
    // Based on feature_gate.py
    const viewLevels = {
      starter: "hidden",
      plus: "current",
      pro: "full",
      free_trial: "full"
    };
    return viewLevels[planType] || "hidden";
  };

  const viewLevel = getViewLevel();

  // Hidden state for Starter users
  if (viewLevel === "hidden") {
    return (
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-purple-700">
            <Map className="w-5 h-5" />
            Learning Path
            <Badge variant="outline" className="ml-auto bg-purple-100 text-purple-700 border-purple-300">
              <Lock className="w-3 h-3 mr-1" />
              Plus+
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-white/80 rounded-lg border border-purple-200">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-violet-500 rounded-full flex items-center justify-center">
              <Map className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-purple-800">Personalized Learning Path</p>
              <p className="text-sm text-purple-600">See your guided learning journey with concept mastery tracking</p>
            </div>
          </div>
          
          {/* Preview (blurred) */}
          <div className="relative">
            <div className="space-y-2 filter blur-sm opacity-50">
              <div className="flex items-center gap-2 p-2 bg-white/50 rounded">
                <Circle className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Introduction to Fractions</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white/50 rounded">
                <Circle className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Equivalent Fractions</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white/50 rounded">
                <Circle className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Adding Fractions</span>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Lock className="w-8 h-8 text-purple-400" />
            </div>
          </div>
          
          <Button
            onClick={onUpgradeClick}
            className="w-full bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white"
            data-testid="upgrade-for-path-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Unlock Learning Path
          </Button>
          
          <p className="text-xs text-center text-purple-600">
            Plus: Current concept • Pro: Full path view
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-md animate-pulse">
        <CardContent className="py-6">
          <div className="h-32 bg-gray-200 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  // Calculate progress stats
  const totalConcepts = learningPath.length;
  const completedConcepts = progress?.concepts_completed?.length || 0;
  const masteredConcepts = progress?.concepts_mastered?.length || 0;
  const progressPercent = totalConcepts > 0 ? (completedConcepts / totalConcepts) * 100 : 0;

  // Determine which concepts to show
  const getVisibleConcepts = () => {
    if (viewLevel === "full") {
      return learningPath;
    }
    // "current" - show current and adjacent concepts only
    const currentIndex = learningPath.findIndex(c => c.id === currentConcept);
    if (currentIndex === -1) return learningPath.slice(0, 3);
    
    const start = Math.max(0, currentIndex - 1);
    const end = Math.min(learningPath.length, currentIndex + 2);
    return learningPath.slice(start, end);
  };

  const visibleConcepts = getVisibleConcepts();

  return (
    <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-purple-50 to-violet-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-purple-700">
          <Map className="w-5 h-5" />
          Learning Path
          <Badge variant="outline" className="ml-auto bg-purple-100 text-purple-700 border-purple-200">
            Grade {grade} {subject.charAt(0).toUpperCase() + subject.slice(1)}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Overview */}
        <div className="p-3 bg-white/80 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Progress</span>
            <span className="text-sm font-medium text-purple-700">
              {completedConcepts}/{totalConcepts} concepts
            </span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-purple-100" />
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>
              <CheckCircle2 className="w-3 h-3 inline mr-1 text-green-500" />
              {masteredConcepts} mastered
            </span>
            <span>{Math.round(progressPercent)}% complete</span>
          </div>
        </div>

        {/* Current Concept Highlight */}
        {currentConcept && (
          <div className="p-3 bg-gradient-to-r from-purple-100 to-violet-100 rounded-lg border border-purple-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-500 rounded-full flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-purple-600 font-medium">Currently Learning</p>
                <p className="font-medium text-purple-800">
                  {learningPath.find(c => c.id === currentConcept)?.name || currentConcept}
                </p>
              </div>
              <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
            </div>
          </div>
        )}

        {/* Concept List */}
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {visibleConcepts.map((concept, idx) => {
            const isCompleted = progress?.concepts_completed?.includes(concept.id);
            const isMastered = progress?.concepts_mastered?.includes(concept.id);
            const isCurrent = concept.id === currentConcept;
            
            return (
              <div 
                key={concept.id}
                className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                  isCurrent 
                    ? "bg-purple-100 border border-purple-300" 
                    : isCompleted 
                      ? "bg-green-50 border border-green-200"
                      : "bg-white/50 border border-gray-100"
                }`}
              >
                {isMastered ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : isCurrent ? (
                  <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  </div>
                ) : isCompleted ? (
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    isCurrent ? "text-purple-800" : isCompleted ? "text-green-700" : "text-gray-600"
                  }`}>
                    {concept.name}
                  </p>
                  {concept.estimated_minutes && (
                    <p className="text-xs text-gray-400">{concept.estimated_minutes} min</p>
                  )}
                </div>
                {isCurrent && (
                  <Badge className="bg-purple-500 text-white text-xs">Now</Badge>
                )}
              </div>
            );
          })}
        </div>

        {/* Show more indicator for Plus users */}
        {viewLevel === "current" && learningPath.length > 3 && (
          <div className="text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={onUpgradeClick}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-100"
            >
              <Crown className="w-4 h-4 mr-1" />
              Upgrade to see full path
            </Button>
          </div>
        )}

        {/* Stats Row */}
        <div className="flex items-center justify-between pt-2 border-t border-purple-100">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <TrendingUp className="w-3 h-3" />
            {masteredConcepts > 0 && `${masteredConcepts} mastered`}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-purple-600 hover:text-purple-700 text-xs"
            onClick={() => {/* Navigate to full learning path view */}}
          >
            View Details
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default LearningPathCard;
